import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import {
  useAllocateLicenseMutation,
  useGetBusinessQuery,
  useGetLicensesQuery,
} from "../../store/api";
import { hasActiveLicense } from "../../utils/license";

const initialState = {
  planCode: "",
  startsOn: "",
  endsOn: "",
  graceDays: "",
  maxStores: "",
  maxTillsPerStore: "",
  maxKiosksPerStore: "",
};

const AllocateLicense = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { data: business } = useGetBusinessQuery(businessId);
  const { data: licenseData, isLoading: isLicenseLoading } =
    useGetLicensesQuery(businessId, { skip: !businessId });
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [allocateLicense, allocateState] = useAllocateLicenseMutation();
  const isLicenseAlreadyActive = useMemo(
    () => hasActiveLicense(licenseData),
    [licenseData],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(form.planCode || "").trim()) {
      nextErrors.planCode = "Plan code is required.";
    }
    if (!form.startsOn) {
      nextErrors.startsOn = "Start date is required.";
    }
    if (!form.endsOn) {
      nextErrors.endsOn = "End date is required.";
    }

    if (form.startsOn && form.endsOn) {
      const startDate = new Date(form.startsOn);
      const endDate = new Date(form.endsOn);
      if (endDate < startDate) {
        nextErrors.endsOn = "End date must be on or after start date.";
      }
    }

    const graceValue = Number(form.graceDays);
    if (form.graceDays !== "" && (Number.isNaN(graceValue) || graceValue < 0)) {
      nextErrors.graceDays = "Grace days must be 0 or greater.";
    }

    const maxStoresValue = Number(form.maxStores);
    if (
      form.maxStores === "" ||
      Number.isNaN(maxStoresValue) ||
      maxStoresValue < 0
    ) {
      nextErrors.maxStores = "Max stores must be 0 or greater.";
    }

    const maxTillsValue = Number(form.maxTillsPerStore);
    if (
      form.maxTillsPerStore === "" ||
      Number.isNaN(maxTillsValue) ||
      maxTillsValue < 0
    ) {
      nextErrors.maxTillsPerStore =
        "Max tills per store must be 0 or greater.";
    }

    const maxKiosksValue = Number(form.maxKiosksPerStore);
    if (
      form.maxKiosksPerStore === "" ||
      Number.isNaN(maxKiosksValue) ||
      maxKiosksValue < 0
    ) {
      nextErrors.maxKiosksPerStore =
        "Max kiosks per store must be 0 or greater.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    if (!validate()) return;

    try {
      await allocateLicense({
        businessId,
        payload: {
          planCode: String(form.planCode || "").trim().toUpperCase(),
          startsOn: form.startsOn,
          endsOn: form.endsOn,
          graceDays: Number(form.graceDays),
          maxStores: Number(form.maxStores),
          maxTillsPerStore: Number(form.maxTillsPerStore),
          maxKiosksPerStore: Number(form.maxKiosksPerStore),
        },
      }).unwrap();
      navigate(`/businesses/${businessId}/licenses`, {
        state: { licenseUpdated: true },
      });
    } catch (err) {
      setSubmitError(err?.data?.detail || "Failed to allocate license.");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Allocate License"
        subtitle={`Assign a new license to ${business?.businessName || "this business"}.`}
        actions={
          <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      <Card>
        {isLicenseLoading ? (
          <p className="text-sm text-slate-500">Checking license status...</p>
        ) : isLicenseAlreadyActive ? (
          <div className="space-y-4">
            <Alert tone="info">
              This business already has an active license. Use reallocate to
              update the existing license instead of creating a new allocation.
            </Alert>
            <div className="flex gap-3">
              <Link to={`/businesses/${businessId}/licenses/edit`}>
                <Button type="button">Reallocate License</Button>
              </Link>
              <Link to={`/businesses/${businessId}/licenses`}>
                <Button type="button" variant="secondary">
                  Back to Overview
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit} autoComplete="off">
            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Plan Code"
                name="planCode"
                placeholder="PRO"
                value={form.planCode}
                onChange={handleChange}
                error={errors.planCode}
                required
              />
              <Input
                label="Starts On"
                name="startsOn"
                type="date"
                value={form.startsOn}
                onChange={handleChange}
                error={errors.startsOn}
                required
              />
              <Input
                label="Ends On"
                name="endsOn"
                type="date"
                value={form.endsOn}
                onChange={handleChange}
                error={errors.endsOn}
                required
              />
              <Input
                label="Grace Days"
                name="graceDays"
                type="number"
                min="0"
                value={form.graceDays}
                onChange={handleChange}
                error={errors.graceDays}
              />
              <Input
                label="Max Stores"
                name="maxStores"
                type="number"
                min="0"
                value={form.maxStores}
                onChange={handleChange}
                error={errors.maxStores}
                required
              />
              <Input
                label="Max Tills Per Store"
                name="maxTillsPerStore"
                type="number"
                min="0"
                value={form.maxTillsPerStore}
                onChange={handleChange}
                error={errors.maxTillsPerStore}
                required
              />
              <Input
                label="Max Kiosks Per Store"
                name="maxKiosksPerStore"
                type="number"
                min="0"
                value={form.maxKiosksPerStore}
                onChange={handleChange}
                error={errors.maxKiosksPerStore}
                required
              />
            </div>

            {submitError && <Alert tone="error">{submitError}</Alert>}

            <div className="flex gap-3">
              <Button type="submit" disabled={allocateState.isLoading}>
                {allocateState.isLoading ? "Allocating..." : "Allocate License"}
              </Button>
              <Link to={`/businesses/${businessId}/licenses`}>
                <Button type="button" variant="secondary">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default AllocateLicense;
